// src/hooks/useUserManagement.js
import { useState, useCallback, useRef, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { userService } from "../services/userService";
import { ROLES, hasRequiredRole, normalizeRole } from "../utils/roles";

const PAGE_ACCESS_ROLES = [
  ROLES.ADMIN,
  ROLES.SUPERADMIN,
  ROLES.LOCATION_ADMIN,
  ROLES.AUDITOR,
  ROLES.TECHNICIAN,
];
const DEFAULT_ROWS_PER_PAGE = 10;

const DEFAULT_FILTERS = {
  companyId: "",
  departmentId: "",
  locationCode: "",
  role: "",
  isActive: "", // "" | true | false
  sortField: "name",
  sortOrder: "asc",
};

export const useUserManagement = () => {
  const { currentUser, logout } = useAuth();

  const [users, setUsers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [permissionError, setPermissionError] = useState(null);
  const [error, setError] = useState(null);
  const [filters, setFiltersState] = useState(DEFAULT_FILTERS);

  const allUsersRef = useRef([]);
  const searchTimerRef = useRef(null);
  const requestIdRef = useRef(0);

  // SAFE "YOU" IDENTIFIER CHECK
  const isSelfDelete = useCallback(
    (user) => {
      if (!currentUser || !user) return false;

      const currentId = String(
        currentUser.uid || currentUser.id || currentUser.userId || ""
      ).trim();
      const currentEmail = String(currentUser.email || "")
        .toLowerCase()
        .trim();

      const targetId = String(
        user.id || user.userId || user.uid || ""
      ).trim();
      const targetEmail = String(user.email || "").toLowerCase().trim();

      const matchesId = Boolean(currentId && targetId && currentId === targetId);
      const matchesEmail = Boolean(
        currentEmail && targetEmail && currentEmail === targetEmail
      );

      return matchesId || matchesEmail;
    },
    [currentUser]
  );

  const applyClientPipeline = useCallback(
    (
      rawList,
      searchTerm = "",
      pageArg = 0,
      rowsPerPageArg = DEFAULT_ROWS_PER_PAGE
    ) => {
      let list = Array.isArray(rawList) ? [...rawList] : [];

      if (filters.departmentId) {
        list = list.filter(
          (u) => String(u.departmentId || "") === String(filters.departmentId)
        );
      }

      // status precedence: `status` string wins; fallback to `isActive` boolean
      const statusValue = (u) => {
        if (u.status === "active" || u.status === "inactive") return u.status;
        if (u.isActive === true) return "active";
        if (u.isActive === false) return "inactive";
        return "";
      };

      if (filters.isActive === true) {
        list = list.filter((u) => statusValue(u) === "active");
      } else if (filters.isActive === false) {
        list = list.filter((u) => statusValue(u) === "inactive");
      }

      const q = (searchTerm || "").trim().toLowerCase();
      if (q) {
        list = list.filter((u) => {
          const hay = [
            u.name,
            u.email,
            u.role,
            u.companyId,
            u.locationCode,
            u.departmentId,
            u.description,
            // legacy fields — still present on older docs
            u.company,
            u.locationName,
            u.departmentName,
          ]
            .map((v) => String(v || "").toLowerCase())
            .join(" ");
          return hay.includes(q);
        });
      }

      const field = filters.sortField || "name";
      const order = filters.sortOrder === "desc" ? -1 : 1;
      list.sort((a, b) => {
        const av = (a?.[field] ?? "").toString().toLowerCase();
        const bv = (b?.[field] ?? "").toString().toLowerCase();
        if (av < bv) return -1 * order;
        if (av > bv) return 1 * order;
        return 0;
      });

      const total = list.length;
      const start = pageArg * rowsPerPageArg;
      const pageItems = list.slice(start, start + rowsPerPageArg);

      return { items: pageItems, total };
    },
    [filters]
  );

  const fetchUsers = useCallback(
    async (
      searchTerm = "",
      pageArg = 0,
      rowsPerPageArg = DEFAULT_ROWS_PER_PAGE
    ) => {
      if (!currentUser) {
        setLoading(false);
        return;
      }

      if (!hasRequiredRole(currentUser.role, PAGE_ACCESS_ROLES)) {
        setPermissionError("Only Admins and Super Admins can access this page");
        setLoading(false);
        return;
      }

      const requestId = ++requestIdRef.current;
      setLoading(true);
      setError(null);
      setPermissionError(null);

      try {
        const userRole = normalizeRole(currentUser.role);
        const isSuperAdmin = userRole === ROLES.SUPERADMIN;

        const fetchArgs = { currentUser };

        // Company scope
        if (isSuperAdmin && filters.companyId) {
          fetchArgs.companyId = filters.companyId;
        }
        // Non-SuperAdmins are auto-scoped by the service; no need to pass.

        // Location scope
        if (filters.locationCode) {
          fetchArgs.locationCode = filters.locationCode;
        }

        // Department scope
        if (filters.departmentId) {
          fetchArgs.departmentId = filters.departmentId;
        }

        // Role scope
        if (filters.role) {
          fetchArgs.role = filters.role;
        }

        // Status (translated from the boolean UI filter)
        if (filters.isActive === true) {
          fetchArgs.status = "active";
        } else if (filters.isActive === false) {
          fetchArgs.status = "inactive";
        }

        // Ask the service for the FULL filtered list; paging happens client-side.
        fetchArgs.page = 0;
        fetchArgs.rowsPerPage = Number.MAX_SAFE_INTEGER;
        fetchArgs.sortField = filters.sortField;
        fetchArgs.sortOrder = filters.sortOrder;

        const raw = (await userService.fetchUsers(fetchArgs)) || [];

        const rawUsers = raw.map((u) => ({
          ...u,
          id: u.id || u.userId || u.uid,
        }));

        if (requestId !== requestIdRef.current) return;

        allUsersRef.current = rawUsers;

        const { items, total } = applyClientPipeline(
          rawUsers,
          searchTerm,
          pageArg,
          rowsPerPageArg
        );

        setUsers(items);
        setTotalCount(total);
      } catch (err) {
        if (requestId !== requestIdRef.current) return;

        if (
          err.message?.includes("Access denied") ||
          err.message?.includes("Access Denied")
        ) {
          setPermissionError(err.message);
        } else {
          setError(err.message || "Failed to fetch users");
        }
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [currentUser, filters, applyClientPipeline]
  );

  const setFilters = useCallback((updater) => {
    setFiltersState((prev) => ({
      ...prev,
      ...(typeof updater === "function" ? updater(prev) : updater),
    }));
  }, []);

  const clearFilters = useCallback(() => {
    setFiltersState(DEFAULT_FILTERS);
  }, []);

  const searchUsers = useCallback(
    (searchTerm) => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
      searchTimerRef.current = setTimeout(() => {
        if (allUsersRef.current.length > 0) {
          const { items, total } = applyClientPipeline(
            allUsersRef.current,
            searchTerm,
            0,
            DEFAULT_ROWS_PER_PAGE
          );
          setUsers(items);
          setTotalCount(total);
          return;
        }
        fetchUsers(searchTerm, 0, DEFAULT_ROWS_PER_PAGE);
      }, 300);
    },
    [fetchUsers, applyClientPipeline]
  );

  useEffect(() => {
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, []);

  const deleteUser = async (userToDelete) => {
    setDeleteLoading(true);
    const selfDelete = isSelfDelete(userToDelete);
    const targetId =
      userToDelete?.id || userToDelete?.userId || userToDelete?.uid;

    try {
      // Pass currentUser so the service can stamp metadata.
      await userService.deleteUser(targetId, currentUser);

      allUsersRef.current = allUsersRef.current.filter(
        (u) => (u.id || u.userId) !== targetId
      );
      setUsers((prev) =>
        prev.filter((u) => (u.id || u.userId) !== targetId)
      );
      setTotalCount((prev) => Math.max(0, prev - 1));

      if (selfDelete) logout();

      return { success: true, isSelfDelete: selfDelete };
    } catch (err) {
      throw new Error(err.message || "Failed to delete user");
    } finally {
      setDeleteLoading(false);
    }
  };

  return {
    users,
    totalCount,
    loading,
    deleteLoading,
    permissionError,
    error,
    fetchUsers,
    searchUsers,
    deleteUser,
    isSelfDelete,
    filters,
    setFilters,
    clearFilters,
  };
};